import React from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useResetPasswordConfirmPage } from './useResetPasswordConfirmPage';
import LegalAgreementLine from '../../components/LegalAgreementLine';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const ResetPasswordConfirmPage: React.FC = () => {
  const {
    formData,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    error,
    loading,
    resetSuccess,
    token,
    passwordStrength,
    isValidatingToken,
    handleChange,
    handleSubmit,
  } = useResetPasswordConfirmPage();

  if (isValidatingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#030b18] to-slate-950 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
          <p className="mt-4 text-slate-300">Verifying your reset link...</p>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#030b18] to-slate-950 text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md bg-slate-900/75 border border-white/10 rounded-2xl p-6 sm:p-8">
          <div className="mx-auto flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-green-500/20 mb-3 sm:mb-4">
            <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Password Reset Successful!</h2>
          <p className="text-sm sm:text-base text-slate-300 mb-4 sm:mb-6">
            Your password has been updated successfully. You'll be redirected to the login page shortly.
          </p>
          <div className="mt-4 sm:mt-6">
            <Link to="/login" className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!token || (error && !resetSuccess)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#030b18] to-slate-950 text-white flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-2xl sm:text-3xl font-extrabold text-slate-100">
            {error.includes('expired') ? 'Link Expired' : 'Invalid Reset Link'}
          </h2>
        </div>
        <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-slate-900/75 py-6 sm:py-8 px-4 border border-white/10 shadow sm:rounded-lg sm:px-6 lg:px-10 text-center">
            <div className="mx-auto flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-red-500/20">
              <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-400" aria-hidden="true" />
            </div>
            <h3 className="mt-3 text-base sm:text-lg font-medium text-slate-100">
              {error.includes('expired') ? 'This password reset link has expired' : 'This password reset link is invalid'}
            </h3>
            <div className="mt-2 text-xs sm:text-sm text-slate-300"><p>{error}</p></div>
            <div className="mt-4 sm:mt-6">
              <Link to="/reset-password" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-xs sm:text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500">
                Request a new password reset
              </Link>
            </div>
            <div className="mt-3 sm:mt-4">
              <Link to="/login" className="text-xs sm:text-sm font-medium text-cyan-400 hover:text-cyan-300">
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-[#030b18] to-slate-950 text-white min-h-screen flex items-center"
      >
        <div className="absolute inset-0 overflow-hidden">
          <motion.div className="absolute -right-64 -top-64 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: 'easeOut' }} />
          <motion.div className="absolute -left-64 -bottom-64 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="text-center lg:text-left">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/5 border border-white/10 backdrop-blur-sm mb-6">
                <span className="h-2 w-2 rounded-full bg-cyan-400 mr-2"></span>
                Reset Your Password
              </motion.div>
              <motion.h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4 sm:mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <span className="block">Create a New</span>
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Password</span>
              </motion.h1>
              <motion.p className="text-base sm:text-lg text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                Please enter your new password below. Make sure it's secure and easy for you to remember.
              </motion.p>
            </motion.div>
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="bg-slate-900/75 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/10 shadow-lg">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">Reset Password</h2>
              {error && (
                <div className="bg-red-500/20 border border-red-400 text-red-100 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg mb-4 sm:mb-6 text-sm">{error}</div>
              )}
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">New Password</label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-white/10 bg-slate-950/70 text-white rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                    />
                    <button type="button" className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeSlashIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" /> : <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />}
                    </button>
                  </div>
                  {formData.password && (
                    <div className="mt-2">
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className={`h-1 rounded-full ${passwordStrength.strength >= i ? (passwordStrength.strength >= 4 ? 'bg-green-500' : passwordStrength.strength >= 3 ? 'bg-yellow-500' : 'bg-red-500') : 'bg-gray-700'}`} />
                        ))}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 sm:gap-x-4 gap-y-1 text-xs text-slate-400">
                        <div className="flex items-center">{passwordStrength.hasMinLength ? <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1" /> : <div className="h-3 w-3 sm:h-4 sm:w-4 rounded-full border-2 border-slate-600 mr-1" />}<span>8+ characters</span></div>
                        <div className="flex items-center">{passwordStrength.hasNumber ? <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1" /> : <div className="h-3 w-3 sm:h-4 sm:w-4 rounded-full border-2 border-slate-600 mr-1" />}<span>Number</span></div>
                        <div className="flex items-center">{passwordStrength.hasUpperCase ? <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1" /> : <div className="h-3 w-3 sm:h-4 sm:w-4 rounded-full border-2 border-slate-600 mr-1" />}<span>Uppercase</span></div>
                        <div className="flex items-center">{passwordStrength.hasSpecialChar ? <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1" /> : <div className="h-3 w-3 sm:h-4 sm:w-4 rounded-full border-2 border-slate-600 mr-1" />}<span>Special char</span></div>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-1">Confirm New Password</label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`appearance-none block w-full px-3 py-2 border ${formData.confirmPassword ? (formData.password === formData.confirmPassword ? 'border-green-500 bg-green-900/20' : 'border-red-500 bg-red-900/20') : 'border-white/10 bg-slate-950/70'} text-white rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm`}
                    />
                    <button type="button" className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? <EyeSlashIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" /> : <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />}
                    </button>
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && <p className="mt-1 text-xs sm:text-sm text-red-400">Passwords do not match</p>}
                </div>
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading || !passwordStrength.isValid || formData.password !== formData.confirmPassword}
                    className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${loading || !passwordStrength.isValid || formData.password !== formData.confirmPassword ? 'bg-gray-700 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200`}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>
              <LegalAgreementLine className="mt-4 sm:mt-6 text-xs text-center text-slate-400" />
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default ResetPasswordConfirmPage;
